import { ApiError, apiFetch, parseErrorDetail } from "@/api/fetch";
import type { FastDataset, FastEvent, FastProgress } from "./types";

/**
 * A logged-out visitor's one dataset: the server returns a claim token with the build's first event; the token,
 * kept in this browser, reads the dataset until the visitor signs in and the page claims it for their account.
 */
const BASE = "/api/fast-datasets/anonymous";
const KEY = "paperclip.datasets.anonymous";
export const TOKEN_HEADER = "X-Claim-Token";

export type AnonClaim = { id: string; token: string };

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const storage = (): Store | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

export const readAnonClaim = (store: Store | null = storage()): AnonClaim | null => {
  try {
    const value: unknown = JSON.parse(store?.getItem(KEY) ?? "null");
    if (value && typeof value === "object" && "id" in value && "token" in value) {
      const { id, token } = value as Record<string, unknown>;
      if (typeof id === "string" && typeof token === "string" && id && token) return { id, token };
    }
  } catch {
    /* unreadable: as if none */
  }
  return null;
};

export const saveAnonClaim = (claim: AnonClaim, store: Store | null = storage()) => {
  try {
    store?.setItem(KEY, JSON.stringify(claim));
  } catch {
    /* private mode: the dataset reads only while the page holds its stream */
  }
};

export const clearAnonClaim = (store: Store | null = storage()) => {
  try {
    store?.removeItem(KEY);
  } catch {
    /* nothing to clear */
  }
};

/** The claim token that reads this dataset, if this browser built it logged out and has not claimed it yet. */
export const anonTokenFor = (id: string, store: Store | null = storage()) => {
  const claim = readAnonClaim(store);
  return claim?.id === id ? claim.token : null;
};

/** Reads a server-sent event stream of the dataset API until it ends. */
export const readEvents = async (response: Response, onEvent: (event: FastEvent) => void) => {
  if (!response.body) return;
  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) return;
    buffer += value;
    let end = buffer.indexOf("\n\n");
    while (end >= 0) {
      const chunk = buffer.slice(0, end);
      buffer = buffer.slice(end + 2);
      for (const line of chunk.split("\n")) {
        if (line.startsWith("data: ")) onEvent(JSON.parse(line.slice(6)) as FastEvent);
      }
      end = buffer.indexOf("\n\n");
    }
  }
};

/** Builds the visitor's dataset as auto-approve does (the free plan's rows); the dataset event's claim token is kept. */
export const streamAnonBuild = async (question: string, onEvent: (event: FastEvent) => void) => {
  const response = await fetch(BASE, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ question }),
  });
  if (!response.ok) throw new ApiError(await parseErrorDetail(response), response.status);
  await readEvents(response, (event) => {
    if (event.event === "dataset" && event.claim_token) saveAnonClaim({ id: event.id, token: event.claim_token });
    onEvent(event);
  });
};

/** The dataset or its progress read with its claim token; null when the token no longer reads it (claimed). */
export const getAnon = async <T extends FastDataset | FastProgress>(token: string, progress = false): Promise<T | null> => {
  const response = await fetch(progress ? `${BASE}/progress` : BASE, { headers: { [TOKEN_HEADER]: token } });
  if (response.status === 404) return null;
  if (!response.ok) throw new ApiError(await parseErrorDetail(response), response.status);
  return response.json() as Promise<T>;
};

/** Moves the dataset built logged out to the signed-in account; 409 while its build is still starting. */
export const claimAnonDataset = async (token: string): Promise<{ id: string }> => {
  const response = await apiFetch(`${BASE}/claim`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
  if (!response.ok) throw new ApiError(await parseErrorDetail(response), response.status);
  return response.json() as Promise<{ id: string }>;
};

export const loginHref = (next: string) => `/login?next=${encodeURIComponent(next)}`;
